import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { FiUsers, FiX, FiSearch, FiCheck, FiAlertTriangle } from 'react-icons/fi';
import BASE_URL, { IMAGE_DOMAIN } from '../../../../config/config';
import { cabecerasAuth } from '../../../../queries/cliente';

/**
 * Un 404 o un 502 no traen cuerpo JSON, así que sin el código de estado el
 * mensaje genérico no dice nada y hay que ir al inspector para saber qué pasó.
 */
const detalleError = (err, generico) => {
    const delServidor = err.response?.data?.message;
    if (delServidor) return delServidor;
    if (err.response?.status) return `${generico} (HTTP ${err.response.status})`;
    return `${generico} (sin respuesta del servidor)`;
};

/**
 * Elige qué cuentas supervisa un cliente: se abre sobre el responsable y se
 * marcan sus supervisados de golpe, que es como se reparte de verdad (una
 * empresa con sus empleados), en vez de ir empleado por empleado.
 *
 * Lo maneja siempre el equipo de soporte. La jerarquía es de un solo nivel, así
 * que solo se ofrecen cuentas de rol `user` que no supervisen a nadie; las que
 * ya tienen otro responsable se marcan como tales para no robarlas sin querer.
 */
const ModalResponsables = ({ usuario, usuarios = [], onCerrar, onGuardado }) => {
    const { t } = useTranslation();
    const [seleccion, setSeleccion] = useState([]);
    const [padres, setPadres] = useState([]);
    const [busqueda, setBusqueda] = useState('');
    const [cargando, setCargando] = useState(true);
    const [guardando, setGuardando] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        let activo = true;
        (async () => {
            try {
                setCargando(true);
                setError('');
                const resp = await axios.get(`${BASE_URL}/usuarios/${encodeURIComponent(usuario.usuario)}/jerarquia`, cabecerasAuth());
                if (!activo) return;
                setSeleccion((resp.data.hijos || []).map((h) => h.usuario));
                setPadres(resp.data.padres || []);
            } catch (err) {
                if (activo) setError(detalleError(err, t('gestorUsuarios.errorCargarJerarquia')));
            } finally {
                if (activo) setCargando(false);
            }
        })();
        return () => { activo = false; };
    }, [usuario.usuario, t]);

    // Un solo nivel: quien ya está supervisado no puede supervisar a su vez
    const estaSupervisado = padres.length > 0;

    const candidatos = useMemo(() => {
        const texto = busqueda.trim().toLowerCase();
        return usuarios
            .filter((u) => u.role === 'user' && u.usuario !== usuario.usuario)
            .filter((u) => !texto || `${u.nombre || ''} ${u.apellidos || ''} ${u.usuario} ${u.email || ''}`.toLowerCase().includes(texto));
    }, [usuarios, usuario.usuario, busqueda]);

    const alternar = (nombre) => setSeleccion((prev) => (
        prev.includes(nombre) ? prev.filter((u) => u !== nombre) : [...prev, nombre]
    ));

    const guardar = async () => {
        try {
            setGuardando(true);
            setError('');
            const resp = await axios.put(
                `${BASE_URL}/usuarios/${encodeURIComponent(usuario.usuario)}/hijos`,
                { hijos: seleccion },
                cabecerasAuth(),
            );
            onGuardado?.(resp.data);
            onCerrar();
        } catch (err) {
            setError(detalleError(err, t('gestorUsuarios.errorGuardarJerarquia')));
        } finally {
            setGuardando(false);
        }
    };

    const nombreDe = (u) => [u.nombre, u.apellidos].filter(Boolean).join(' ') || u.usuario;
    const bloqueado = usuario.role !== 'user' || estaSupervisado;

    return (
        <div className="gn-modal-overlay" onClick={onCerrar}>
            <div className="gn-modal jer-modal" onClick={(e) => e.stopPropagation()}>
                <div className="gn-modal-header">
                    <h3><FiUsers /> {t('gestorUsuarios.supervisadosDe', { usuario: nombreDe(usuario) })}</h3>
                    <button type="button" className="gn-boton-cerrar" onClick={onCerrar}><FiX /></button>
                </div>

                <div className="jer-cuerpo">
                    <p className="jer-sub">{t('gestorUsuarios.supervisadosTexto')}</p>

                    {usuario.role !== 'user' ? (
                        <div className="jer-aviso">
                            <FiAlertTriangle aria-hidden="true" />
                            <p>{t('gestorUsuarios.jerarquiaSoloClientes')}</p>
                        </div>
                    ) : estaSupervisado ? (
                        <div className="jer-aviso">
                            <FiAlertTriangle aria-hidden="true" />
                            <p>{t('gestorUsuarios.yaEstaSupervisado', { usuario: padres.map(nombreDe).join(', ') })}</p>
                        </div>
                    ) : (
                        <>
                            <div className="gn-campo-icono">
                                <FiSearch className="gn-icono-input" />
                                <input
                                    type="text"
                                    value={busqueda}
                                    onChange={(e) => setBusqueda(e.target.value)}
                                    placeholder={t('gestorUsuarios.buscarSupervisado')}
                                    className="campo-busqueda gn-input-con-icono"
                                />
                            </div>

                            <div className="jer-lista">
                                {cargando ? (
                                    <p className="jer-vacio">{t('gestorUsuarios.cargandoJerarquia')}</p>
                                ) : candidatos.length === 0 ? (
                                    <p className="jer-vacio">{t('gestorUsuarios.sinCandidatos')}</p>
                                ) : candidatos.map((candidato) => {
                                    const marcado = seleccion.includes(candidato.usuario);
                                    return (
                                        <label key={candidato.usuario} className={`jer-opcion ${marcado ? 'marcado' : ''}`}>
                                            <input type="checkbox" checked={marcado} onChange={() => alternar(candidato.usuario)} />
                                            <img
                                                src={candidato.fotoPerfil && candidato.fotoPerfil !== 'none'
                                                    ? (candidato.fotoPerfil.startsWith('http') ? candidato.fotoPerfil : `${IMAGE_DOMAIN}${candidato.fotoPerfil}`)
                                                    : `${IMAGE_DOMAIN}/images/default-user.png`}
                                                alt=""
                                                aria-hidden="true"
                                            />
                                            <span className="jer-datos">
                                                <strong>{nombreDe(candidato)}</strong>
                                                <small>@{candidato.usuario} · {candidato.email}</small>
                                            </span>
                                        </label>
                                    );
                                })}
                            </div>
                        </>
                    )}

                    {error && <p className="jer-error">{error}</p>}
                </div>

                <div className="jer-pie">
                    <span>{t('gestorUsuarios.supervisadosSeleccionados', { count: seleccion.length })}</span>
                    <div className="botones-formulario">
                        <button type="button" className="gn-boton-cancelar" onClick={onCerrar}>{t('comun.cancelar')}</button>
                        <button
                            type="button"
                            className="boton-primario"
                            onClick={guardar}
                            disabled={guardando || cargando || bloqueado}
                        >
                            <FiCheck /> {guardando ? t('comun.guardando') : t('comun.guardar')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ModalResponsables;
